import {
  Card,
  FlatActionList,
  FlatActionListItem,
} from "@towersystems/roam-common-native";
import {
  ProductLocationInventoryPricing,
  SaleAdjustmentType,
} from "@towersystems/roam-common/lib/generated-types";
import { Colors, Text, View } from "react-native-ui-lib";
import Product from "../../../../../utilities/data/model/Product";
import SaleItem from "../../../../../utilities/data/model/SaleItem";
import { moneyToFormattedString } from "../../../../../utilities/money";
import { useApplyTradePriceAction } from "../../../utils/sale-item";
import { EditCartItemModalScreenProps, ProductState } from "../types";

export type PricingListCardProps = Pick<
  EditCartItemModalScreenProps,
  "navigation" | "route"
> & {
  item: SaleItem;
  productState: ProductState;
};

export function PricingListCard({
  navigation,
  item,
  productState,
}: PricingListCardProps) {
  const { product, kitItemDetails } = productState;

  const { applyTradePrice } = useApplyTradePriceAction();

  const saleDiscount = item.findAdjustmentByType(
    SaleAdjustmentType.SALE_DISCOUNT
  );

  const loyaltyDiscount = item.findAdjustmentByType(
    SaleAdjustmentType.LOYALTY_DISCOUNT
  );

  const giftVoucherDiscount = item.findAdjustmentByType(
    SaleAdjustmentType.GIFT_VOUCHER_CODE
  );

  const catalogueDiscount = item.findAdjustmentByType(
    SaleAdjustmentType.CATALOGUE_DISCOUNT
  );

  const tradePriceAdjustment = item.findAdjustmentByType(
    SaleAdjustmentType.TRADE_PRICE_ADJUSTMENT
  );

  const handleOnRemoveDiscount = async (adjustmentType: SaleAdjustmentType) => {
    return item.removeAdjustment(adjustmentType).catch((e) => console.log(e));
  };

  const handleApplyTradePrice = async () => {
    try {
      await applyTradePrice(item);
    } catch (err) {
      console.log(err);
    }
  };

  const pricing: ProductLocationInventoryPricing | undefined =
    product.locationInventoryPricing[0];

  const discountable = product.discountable;

  const canAdjustTradePrice =
    !product.internal && pricing?.tradePrice > 0 && !tradePriceAdjustment;

  let items: FlatActionListItem[] = [
    {
      key: "1",
      title: "Product Unit Price",
      componentRight(icon: any) {
        return (
          <View style={{ alignItems: "flex-end" }}>
            <Text style={{ fontSize: 20 }}>
              {moneyToFormattedString(item.price)}
            </Text>
            {item.hasAdjustments ? (
              <Text
                style={{
                  fontSize: 18,
                  opacity: 0.7,
                  textDecorationLine: "line-through",
                }}
              >
                {moneyToFormattedString(item.basePrice)}
              </Text>
            ) : undefined}
          </View>
        );
      },
    },
  ];

  // cost price doesn't apply to kits
  if (!product.isKit) {
    items.push({
      key: "2",
      title: "Product Cost Price",
      componentRight(icon: any) {
        return (
          <View style={{ alignItems: "flex-end" }}>
            <Text style={{ fontSize: 20 }}>
              {moneyToFormattedString(pricing?.costPrice ?? 0)}
            </Text>
          </View>
        );
      },
    });
  }

  if (product.isKit) {
    kitItemDetails?.forEach(({ product, kitItem: productKitItem }) => {
      const name = `${productKitItem.quantity} × ${
        product.displayName || product.name
      }`;

      items.push({
        key: `kit-item-${productKitItem.id}`,
        title: `${name} @`,
        componentRight(icon: any) {
          return (
            <View style={{ alignItems: "flex-end" }}>
              <Text style={{ fontSize: 20 }}>
                {moneyToFormattedString(productKitItem?.price ?? 0)}
              </Text>
            </View>
          );
        },
      });
    });
  }

  if (discountable && !tradePriceAdjustment) {
    items.push(
      item.findAdjustmentByType(SaleAdjustmentType.MANUAL_DISCOUNT)
        ? {
            key: "removeDiscount",
            title: "Remove Line Discount",
            onPress: () => {
              handleOnRemoveDiscount(SaleAdjustmentType.MANUAL_DISCOUNT);
            },
            destructive: true,
            componentRight(icon: any) {
              return (
                <View style={{ alignItems: "center" }} row>
                  <Text style={[{ fontSize: 20, color: Colors.destructive }]}>
                    {moneyToFormattedString(
                      item.findAdjustmentByType(
                        SaleAdjustmentType.MANUAL_DISCOUNT
                      )?.amount
                    )}
                  </Text>
                  {icon}
                </View>
              );
            },
          }
        : {
            key: "applyLineDiscount",
            title: "Apply Line Discount",
            onPress: () => navigation.navigate("DiscountCartItemModalScreen"),
          }
    );
  }

  if (canAdjustTradePrice && !tradePriceAdjustment) {
    items.push({
      key: "applyTradePrice",
      title: "Apply Trade Price",
      componentRight(icon: any) {
        return (
          <View style={{ alignItems: "center" }} row>
            <Text style={[{ fontSize: 20, color: Colors.action }]}>
              {moneyToFormattedString(pricing?.tradePrice ?? 0)}
            </Text>
            {icon}
          </View>
        );
      },
      onPress: () => handleApplyTradePrice(),
    });
  } else if (tradePriceAdjustment) {
    items.push({
      key: "removeTradePrice",
      title: "Remove Trade Price",
      onPress: () => {
        handleOnRemoveDiscount(SaleAdjustmentType.TRADE_PRICE_ADJUSTMENT);
      },
      destructive: true,
      componentRight(icon: any) {
        return (
          <View style={{ alignItems: "center" }} row>
            <Text style={[{ fontSize: 20, color: Colors.destructive }]}>
              {moneyToFormattedString(tradePriceAdjustment.amount)}
            </Text>
            {icon}
          </View>
        );
      },
    });
  }

  if (saleDiscount) {
    items = [
      ...items,
      saleDiscount && {
        key: "removeSaleDiscount",
        title: "Remove Sale Discount",
        onPress: () => {
          handleOnRemoveDiscount(SaleAdjustmentType.SALE_DISCOUNT);
        },
        destructive: true,
        componentRight(icon: any) {
          return (
            <View style={{ alignItems: "center" }} row>
              <Text style={[{ fontSize: 20, color: Colors.destructive }]}>
                {moneyToFormattedString(saleDiscount.amount)}
              </Text>
              {icon}
            </View>
          );
        },
      },
    ];
  }

  if (giftVoucherDiscount) {
    items = [
      ...items,
      giftVoucherDiscount && {
        key: "removeGiftVoucherDiscount",
        title: "Remove Gift Voucher Discount",
        onPress: () => {
          handleOnRemoveDiscount(SaleAdjustmentType.GIFT_VOUCHER_CODE);
        },
        destructive: true,
        componentRight(icon: any) {
          return (
            <View style={{ alignItems: "center" }} row>
              <Text style={[{ fontSize: 20, color: Colors.destructive }]}>
                {moneyToFormattedString(giftVoucherDiscount.amount)}
              </Text>
              {icon}
            </View>
          );
        },
      },
    ];
  }

  if (catalogueDiscount) {
    items = [
      ...items,
      catalogueDiscount && {
        key: "removeCatalogueDiscount",
        title: "Remove Catalogue Discount",
        onPress: () => {
          handleOnRemoveDiscount(SaleAdjustmentType.CATALOGUE_DISCOUNT);
        },
        destructive: true,
        componentRight(icon: any) {
          return (
            <View style={{ alignItems: "center" }} row>
              <Text style={[{ fontSize: 20, color: Colors.destructive }]}>
                {moneyToFormattedString(catalogueDiscount.amount)}
              </Text>
              {icon}
            </View>
          );
        },
      },
    ];
  }

  if (loyaltyDiscount) {
    items = [
      ...items,
      loyaltyDiscount && {
        key: "removeLoyaltyDiscount",
        title: "Remove Loyalty Discount",
        onPress: () => {
          handleOnRemoveDiscount(SaleAdjustmentType.LOYALTY_DISCOUNT);
        },
        destructive: true,
        componentRight(icon: any) {
          return (
            <View style={{ alignItems: "center" }} row>
              <Text style={[{ fontSize: 20, color: Colors.destructive }]}>
                {moneyToFormattedString(loyaltyDiscount.amount)}
              </Text>
              {icon}
            </View>
          );
        },
      },
    ];
  }

  return (
    <Card titlePosition="outside" title="Pricing">
      <FlatActionList items={items} />
    </Card>
  );
}
